import moment from 'moment'
import { uniqueId } from 'lodash'
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FocusEventHandler,
  type KeyboardEvent,
  type MouseEvent,
} from 'react'
import { DATE_PICKER_YEAR_INTERACTION_SELECTORS } from '../../utils/constants'
import {
  getDatePickerIconSelector,
  parseManualDateTimeInput,
  shouldKeepDatePickerOpenOnOutsideClick,
} from '../../utils/datePickerHelpers'
import { CustomCalendarContainer } from './CustomCalendarContainer'

const useYearDropdownInteractionGuard = (isOpen: boolean) => {
  useEffect(() => {
    if (!isOpen) return

    const onMouseDownCapture = (event: globalThis.MouseEvent) => {
      const target = event.target as HTMLElement
      const yearListOpen = document.querySelector(
        '.react-datepicker__year-dropdown'
      )
      if (!yearListOpen) return

      const isYearInteraction = DATE_PICKER_YEAR_INTERACTION_SELECTORS.some(
        (selector) => target.closest(selector)
      )
      if (isYearInteraction) {
        event.stopImmediatePropagation()
      }
    }

    document.addEventListener('mousedown', onMouseDownCapture, true)
    return () => {
      document.removeEventListener('mousedown', onMouseDownCapture, true)
    }
  }, [isOpen])
}

export const useStableCalendarContainer = (
  showClear: boolean | undefined,
  onPickerChange: (date: Date | null) => void,
  onClose: () => void
) => {
  const onPickerChangeRef = useRef(onPickerChange)
  onPickerChangeRef.current = onPickerChange
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  return useCallback(
    (props: { className?: string; children?: React.ReactNode }) => (
      <CustomCalendarContainer
        className={props.className}
        showClear={showClear}
        onClear={() => {
          onPickerChangeRef.current(null)
          onCloseRef.current()
        }}
        onTodayClick={() => {
          onPickerChangeRef.current(moment().toDate())
          onCloseRef.current()
        }}
      >
        {props.children}
      </CustomCalendarContainer>
    ),
    [showClear]
  )
}

export const useDatePickerOpenState = (name?: string) => {
  const [isOpen, setIsOpen] = useState(false)
  useYearDropdownInteractionGuard(isOpen)

  const uniqueString = useMemo(() => uniqueId(name ?? ''), [name])
  const iconSelector = useMemo(
    () => getDatePickerIconSelector(uniqueString),
    [uniqueString]
  )

  const closeCalendar = useCallback(() => setIsOpen(false), [])
  const openCalendar = useCallback(() => setIsOpen(true), [])
  const toggleCalendar = useCallback(() => setIsOpen((prev) => !prev), [])

  const handleClickOutside = useCallback(
    (event: globalThis.MouseEvent) => {
      const target = event.target as HTMLElement
      if (shouldKeepDatePickerOpenOnOutsideClick(target, iconSelector)) {
        return
      }
      setIsOpen(false)
    },
    [iconSelector]
  )

  return {
    isOpen,
    setIsOpen,
    uniqueString,
    closeCalendar,
    openCalendar,
    toggleCalendar,
    handleClickOutside,
  }
}

/** Matches react-datepicker `onChangeRaw` (typing still delivers an input change event at runtime). */
export type DatePickerOnChangeRawEvent =
  | MouseEvent<HTMLElement>
  | KeyboardEvent<HTMLElement>

const getInputValueFromRawEvent = (
  event?: DatePickerOnChangeRawEvent
): string | null => {
  const target = event?.target
  if (target instanceof HTMLInputElement) {
    return target.value
  }
  if (target && 'value' in target && typeof target.value === 'string') {
    return target.value
  }
  return null
}

/** Commit typed date-time; do not bind DatePicker `value` or typing is blocked. */
export const useManualDateTimeInputHandlers = (
  handleChange: ((date?: string | null) => void) | undefined,
  isDisabled: boolean | undefined,
  timeZone: string
) => {
  const commitManualDateTime = useCallback(
    (rawValue: string) => {
      if (!handleChange || isDisabled) return
      const trimmed = rawValue.trim()
      if (!trimmed) {
        handleChange('')
        return
      }
      const parsedIso = parseManualDateTimeInput(trimmed, timeZone)
      if (parsedIso !== null) {
        handleChange(parsedIso)
      }
    },
    [handleChange, isDisabled, timeZone]
  )

  const handleChangeRaw = useCallback(
    (event?: DatePickerOnChangeRawEvent) => {
      const inputValue = getInputValueFromRawEvent(event)
      if (inputValue === null) return
      commitManualDateTime(inputValue)
    },
    [commitManualDateTime]
  )

  const handleInputBlur: FocusEventHandler<HTMLElement> = useCallback(
    (event) => {
      const target = event.target
      if (target instanceof HTMLInputElement) {
        commitManualDateTime(target.value)
      }
    },
    [commitManualDateTime]
  )

  return { handleChangeRaw, handleInputBlur }
}

/** Calendar opens from icon only; field click/focus stays typeable. */
export const MANUAL_DATE_TIME_PICKER_INPUT_PROPS = {
  preventOpenOnFocus: true,
  strictParsing: false,
} as const
